/**
 * Renders a small black cube at the origin of every scene object.
 * Depth-tested by default so origins are occluded by geometry.
 * In object-mode X-ray they ignore depth so hidden origins stay visible.
 * Selected objects are full opacity; unselected are dimmed.
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import { entityIdToStoreKey, useEntityRegistry } from "../ecs/index.ts";
import { screenToWorldRadius } from "./screenRadius.ts";

/** Desired screen size of the origin cube in pixels. */
const ORIGIN_DOT_PX = 1;

/** Half-extent of the box geometry (arbitrary; scaled per-frame). */
const BOX_HALF = 0.5;

const geometry = new THREE.BoxGeometry(
  BOX_HALF * 2,
  BOX_HALF * 2,
  BOX_HALF * 2,
);

const materialSelected = new THREE.MeshBasicMaterial({
  color: 0x000000,
  depthTest: true,
  depthWrite: false,
  transparent: true,
  opacity: 1,
});

const materialDimmed = new THREE.MeshBasicMaterial({
  color: 0x000000,
  depthTest: true,
  depthWrite: false,
  transparent: true,
  opacity: 0.25,
});

const materialSelectedXray = new THREE.MeshBasicMaterial({
  color: 0x000000,
  depthTest: false,
  depthWrite: false,
  transparent: true,
  opacity: 1,
});

const materialDimmedXray = new THREE.MeshBasicMaterial({
  color: 0x000000,
  depthTest: false,
  depthWrite: false,
  transparent: true,
  opacity: 0.25,
});

export function OriginDots() {
  const selectedObjects = useSelectedLeafObjectIds();
  const editMode = useStore((s) => s.editMode);
  const poseMode = useStore((s) => s.poseMode);
  const objectXray = useStore(
    (s) => s.workspaceMode === "object" && s.objectXray,
  );
  const registry = useEntityRegistry();

  const { camera, size: viewportSize } = useThree();
  const groupRef = useRef<THREE.Group>(null);

  // Don't show origin dots in edit or pose mode
  if (editMode != null || poseMode != null) return null;

  // Collect all objects with their selection state via ECS
  const entries: {
    key: string;
    pos: [number, number, number];
    selected: boolean;
  }[] = [];
  for (const [, entity] of registry) {
    const t = entity.components.transform;
    if (!t) continue;
    if (entity.components.visibility?.visible === false) continue;
    const storeKey = entityIdToStoreKey(entity.id);
    entries.push({
      key: storeKey,
      pos: t.position as [number, number, number],
      selected: selectedObjects.has(storeKey),
    });
  }

  if (entries.length === 0) return null;

  return (
    <group ref={groupRef} userData={{ excludeFromExport: true }}>
      {entries.map(({ key, pos, selected }) => (
        <OriginDot
          key={key}
          position={pos}
          selected={selected}
          xray={objectXray}
          camera={camera}
          viewportSize={viewportSize}
        />
      ))}
    </group>
  );
}

function OriginDot({
  position,
  selected,
  xray,
  camera,
  viewportSize,
}: {
  position: [number, number, number];
  selected: boolean;
  xray: boolean;
  camera: THREE.Camera;
  viewportSize: { width: number; height: number };
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const material = xray
    ? selected
      ? materialSelectedXray
      : materialDimmedXray
    : selected
      ? materialSelected
      : materialDimmed;

  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    mesh.scale.setScalar(
      screenToWorldRadius(
        ORIGIN_DOT_PX,
        mesh.position,
        camera,
        viewportSize.height,
      ) / BOX_HALF,
    );
  });

  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      material={material}
      position={position}
      renderOrder={998}
    />
  );
}
